import { BusinessObjectBase } from './base';
import { tokenLinkSchema } from '@schemas/business-objects/token-link';
import type { Tenant } from './tenant';
import type { Token } from './token';
import type { Id } from '../base-types';

export interface TokenLinkData {
  readonly id: Id;
  readonly downstreamTokenId: Id;
  readonly downstreamToken?: Token;
  readonly upstreamTokenId: Id;
  readonly upstreamToken?: Token;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
}

export interface TokenLink extends Readonly<TokenLinkData> {}
type TokenLinkCreateWithChildrenData = Partial<TokenLinkData>;

export class TokenLink extends BusinessObjectBase {
  static readonly entityName = 'TokenLink' as const;
  static readonly plural = 'token-links';
  static schema = tokenLinkSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<TokenLink[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new TokenLink(data));
  }

  static async findOne(filter?: any): Promise<TokenLink | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new TokenLink(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<TokenLink> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new TokenLink(data);
  }

  static async create(data: Omit<TokenLinkData, 'id'>): Promise<TokenLink> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new TokenLink(result);
  }

  static async createMany(
    data: Omit<TokenLinkData, 'id'>[],
  ): Promise<TokenLink[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new TokenLink(item));
  }

  static async createWithChildren(
    data: TokenLinkCreateWithChildrenData,
  ): Promise<TokenLink> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new TokenLink(result);
  }

  static async createManyWithChildren(
    data: TokenLinkCreateWithChildrenData[],
  ): Promise<TokenLink[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new TokenLink(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<TokenLinkData>,
  ): Promise<TokenLink> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new TokenLink(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }

  // --- Behaviors ---

  static async findByDownstream(options: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/find-by-downstream`;
    return this.post<any>(url, options);
  }
  static async findByUpstream(options: any): Promise<any> {
    const url = `${this.baseUrl}/${this.plural}/find-by-upstream`;
    return this.post<any>(url, options);
  }
}
